import { useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import type { LimitationDefinition, LimitationValue } from "src/shared/api-types/limitations";

import Button from "src/components/Button";

import { getDefaultValueForType, normalizeOptionOrder, normalizeOptionValue } from "../limitationFormValues";
import type { LimitationFormValues } from "../types";

interface LimitationValueEditorProps {
  type: LimitationDefinition["type"];
  options: LimitationFormValues["options"];
  structuralLocked: boolean;
  onChange: (options: LimitationFormValues["options"]) => void;
}

const LimitationValueEditor = ({ type, options, structuralLocked, onChange }: LimitationValueEditorProps) => {
  const { t } = useTranslation();

  const handleAdd = () => {
    onChange(
      normalizeOptionOrder([
        ...options,
        {
          value: getDefaultValueForType(type),
          sortOrder: options.length,
          archived: false,
        },
      ]),
    );
  };

  const handleChange = (index: number, value: string | number | boolean) => {
    const next = options.map((option, optionIndex) =>
      optionIndex === index
        ? {
            ...option,
            value: normalizeOptionValue(type, value as LimitationValue),
          }
        : option,
    );
    onChange(normalizeOptionOrder(next));
  };

  const handleRemove = (index: number) => {
    const option = options[index];
    if (option?.id) {
      const next = options.map((item, optionIndex) => (optionIndex === index ? { ...item, archived: true } : item));
      onChange(normalizeOptionOrder(next));
      return;
    }

    onChange(normalizeOptionOrder(options.filter((_, optionIndex) => optionIndex !== index)));
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= options.length) return;
    const next = [...options];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    onChange(normalizeOptionOrder(next));
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle2">{t("limitations.values")}</Typography>
        <Button size="small" onClick={handleAdd} startIcon={<AddIcon />}>
          {t("limitations.add_value")}
        </Button>
      </Box>
      {options.filter((option) => !option.archived).length === 0 && (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          {t("limitations.no_values_configured")}
        </Typography>
      )}
      {options.map((option, index) => {
        if (option.archived) return null;
        const optionInUse = !!option.id && (option.usageCount ?? 0) > 0;
        const valueLocked = optionInUse && type !== "list";
        const optionKey = option.id ? `option-${option.id}-${index}` : `new-option-${index}`;
        const showMoveActions = type === "list";
        const moveUpDisabled = type !== "list" || index === 0;
        const moveDownDisabled = type !== "list" || index === options.length - 1;
        return (
          <Box
            key={optionKey}
            sx={{
              display: "grid",
              gridTemplateColumns: showMoveActions ? "2rem 2rem minmax(10rem, 1fr) 2rem" : "minmax(10rem, 1fr) 2rem",
              gap: 1,
              alignItems: "center",
              mb: 1,
            }}
          >
            {showMoveActions && (
              <IconButton
                size="small"
                title={t("limitations.move_up")}
                onClick={() => handleMove(index, -1)}
                aria-disabled={moveUpDisabled}
                sx={{ color: moveUpDisabled ? "action.disabled" : "text.primary" }}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            )}
            {showMoveActions && (
              <IconButton
                size="small"
                title={t("limitations.move_down")}
                onClick={() => handleMove(index, 1)}
                aria-disabled={moveDownDisabled}
                sx={{ color: moveDownDisabled ? "action.disabled" : "text.primary" }}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            )}
            {type === "boolean" ? (
              <TextField
                select
                size="small"
                label={t("limitations.value")}
                value={String(option.value)}
                onChange={(event) => handleChange(index, event.target.value === "true")}
                disabled={structuralLocked || valueLocked}
                sx={{ minWidth: 120 }}
              >
                <MenuItem value="true">{t("yes")}</MenuItem>
                <MenuItem value="false">{t("no")}</MenuItem>
              </TextField>
            ) : (
              <TextField
                size="small"
                type={type === "number" ? "number" : "text"}
                label={t("limitations.value")}
                value={String(option.value)}
                onChange={(event) => handleChange(index, event.target.value)}
                disabled={valueLocked}
                autoComplete="off"
              />
            )}
            {!optionInUse && (
              <IconButton onClick={() => handleRemove(index)} size="small">
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default LimitationValueEditor;
